import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Stack, Button, Card, CardContent, Typography, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TabContext, TabList } from '@mui/lab';
import { ROUTE_PATH } from '../../../constants/route-constants';
import NoData from '../no-data';
import InfiniteScroll from 'react-infinite-scroller';
import SkeletonLoader from '../../common/skeleton-loader';
import PropTypes from 'prop-types';
import { INFO_POP_UP_DETAILS, INFO_POP_UP_TEXT, PAGE_SIZE, SECTION_KEYS } from '../../../constants/app-constants';
import CardHeader from '../auction-card/card-header';
import { makePriceLabelRoundAE } from '../../../utils/helpers/make-price-label';
import { ReactComponent as CarImage } from './car-img.svg';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { prepareGADataAndSendEvent, trackDealerEvents } from '../../../tracking';
import { trackWebViewEvents } from '../../../tracking/webview-tracking';
import { getItem } from '../../../utils/helpers/storage-crud';

const MyOrders = ({ fetchWonDealsConnect }) => {
    const { user } = useSelector(state => state);
    const [myOrders, setMyOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [tabIndex, setTabIndex] = useState('0');
    const [open, setOpen] = useState(false);

    const { dealerCode, dealerType, mobilePhone: phone, access_token: accessToken } = user || {};
    const modalStyles = {
        position: 'fixed',
        bottom: open ? '0' : '-100%',
        borderRadius: '5px 5px 0 0',
        left: '0',
        width: '100%',
        height: '200px',
        backgroundColor: 'white',
        boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.2)',
        transition: 'bottom 0.3s ease-in-out',
        zIndex: '999',
    };

    const contentStyles = {
        padding: '16px',
    };
    const ref = useRef({ currentPage: 0, pageSize: PAGE_SIZE });
    const navigate = useNavigate();
    const location = useLocation();
    const isWebView = getItem('isWebView');

    const sectionKey = SECTION_KEYS[tabIndex];
    useEffect(() => {
        if (!!tabIndex) {
            handleRefresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIndex]);
    const showInfocard = location.pathname === ROUTE_PATH.ORDERS;

    const handleCardClick = (auction) => {
        const { appointmentId, dealer: { auctionId } = {} } = auction || {};
        if (isWebView) {
            trackWebViewEvents('deepLink', {
                data: `cars24aepartner://?path=auction-details&auctionId=${auctionId}&appointmentId=${appointmentId}`,
            });
            return;
        }
        navigate(`${ROUTE_PATH.ORDERS}${ROUTE_PATH.AUCTION_DETAILS}?aid=${appointmentId}`);
    };

    const setInitialData = (data, isPaginate) => {
        setMyOrders(isPaginate ? [...myOrders, ...data?.purchaseRequests] : data?.purchaseRequests);
        setTotalCount(data?.totalCount);
    };

    const makePayload = () => {
        return { page: ref.current.currentPage, size: ref.current.pageSize, section: SECTION_KEYS[tabIndex] };
    };

    const loadMoreData = async () => {
        try {
            if (myOrders?.length < totalCount && (ref.current.currentPage + 1) === (myOrders?.length / ref.current.pageSize)) {
                setLoading(true);
                ref.current.currentPage += 1;
                const payload = makePayload();
                fetchWonDealsConnect(payload, true).then(res => {
                    setInitialData(res, true);
                }).finally(() => setLoading(false));
            }
        } catch (error) {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setLoading(true);
        ref.current.currentPage = 0;
        fetchWonDealsConnect(makePayload()).then(res => {
            setInitialData(res);
        }).finally(() => setLoading(false));
    };

    const handleSubTitleClick = () => {
        navigate(`${ROUTE_PATH.EXPLORE}`);
    };
    const isDataAvailable = (myOrders && myOrders?.length > 0);

    const titles = [
        'No cars won for now!',
        'No cars under transfer process!',
        'No cars bought by you yet!'
    ];

    useEffect(() => {
        if (accessToken) {
            handleRefresh();
        }
    }, [accessToken]);

    const handleChange = (event, newValue) => {
        const gaEvents = ['myWinsTabClick', 'myDealsTabClick', 'myCarTabClick'];
        setTabIndex(newValue);
        setOpen(false);
        trackDealerEvents(`${gaEvents[newValue]}`, { phone, dealerCode, dealerType });
    };

    const togglePopup = () => {
        setOpen(!open);
        if (!open) {
            prepareGADataAndSendEvent('infoIconClick', user, null, '', { eventLabel: INFO_POP_UP_TEXT[sectionKey] });
        }
    };
    return (
        <div className={` ${isWebView ? 'mb-32' : ''}`}>
            <Stack direction={'row'} spacing={2} alignItems="center" justifyContent={'space-between'} className='mb-8'>
                <div>
                    <span className='px-1'>
                        Won Auctions
                    </span>
                </div>
                <Button size='small' onClick={handleRefresh} className='bg-light-border text-text !mt-3' startIcon={<RefreshIcon />}>
                    Refresh
                </Button>
            </Stack>
            <TabContext value={tabIndex}>
                <TabList variant='fullWidth' onChange={handleChange} aria-label="lab API tabs example">
                    <Tab label="MY WINS" value={'0'} />
                    <Tab label="MY DEALS" value={'1'} />
                    <Tab label="MY CARS" value={'2'} />
                </TabList>
            </TabContext>
            {isDataAvailable &&
                <React.Fragment>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        threshold={50}
                        hasMore={!!myOrders?.length < totalCount}
                    >
                        <Stack spacing={3} direction={'column'}>
                            {myOrders?.map((auction, index) => (
                                <Card key={index} className='shadow-inner' onClick={handleCardClick.bind(null, auction)}>
                                    <CardContent className='flex flex-row justify-start gap-4 p4'>
                                        <CarImage alt='Car Image' className='w-36 h-24 rounded-lg' height={80} width={80} />
                                        <div className='flex flex-col gap-3'>
                                            <CardHeader auction={auction} onlyTitle />
                                            <div className='flex flex-col gap-1'>
                                                <Typography variant='body1' className='font-bold'>Total Price</Typography>
                                                <Typography variant='body1'>{makePriceLabelRoundAE(auction?.price || auction?.dealer?.bidPrice)}</Typography>
                                            </div>
                                        </div>
                                    </CardContent>
                                    <div className='p-4 text-center bg-v-light-br text-primary font-bold flex items-center justify-center gap-2'>
                                        Appointment Id: {auction?.appointmentId}
                                    </div>
                                </Card>
                            ))}
                        </Stack>
                    </InfiniteScroll>
                </React.Fragment>
            }
            {loading && <SkeletonLoader />}
            {!isDataAvailable && !loading && <NoData handleRefresh={handleRefresh} subTitle='View live auctions running' subTitleClass="text-lg mt-8 text-primary" title={titles[tabIndex]} handleSubTitleClick={handleSubTitleClick} />}
            <div>
                {showInfocard && <p className={`flex text-center justify-center ${isWebView ? 'mb-20' : ''}`} >{INFO_POP_UP_TEXT[sectionKey]}<span className='ml-1'><InfoIcon onClick={togglePopup} /></span></p>}
                <div style={modalStyles} onBlur={togglePopup} >
                    {open && <div style={contentStyles}>
                        {INFO_POP_UP_DETAILS[sectionKey]?.map((item, index) => {
                            return (
                                <div key={`pop-up-details-${index}`}>
                                    <h2 >{item?.heading}</h2>
                                    <ol>
                                        {item?.points?.map((point, pointIndex) => {
                                            return (
                                                <li key={`pop-up-points-${index}-${pointIndex}`}>{point}</li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            );
                        })}
                        <CloseIcon className='absolute right-5 top-5' onClick={togglePopup} />
                    </div>}
                </div>
            </div>
        </div>
    );
};

MyOrders.propTypes = {
    fetchWonDealsConnect: PropTypes.func
};

export default MyOrders;
